import { useId, type ReactNode } from 'react';

import { IconDisc } from '../../primitives/IconDisc/IconDisc';
import styles from './Card.module.css';

/**
 * The design language's section card (the dashboard welcome card made
 * reusable): a card ground, and a head row of an `IconDisc` beside a
 * confident heading, with an optional slot at the row's end for the
 * section's own commands or a chip.
 *
 * `CardHead` is the head alone, for a panel that already draws its own card
 * (a module stylesheet with padding and a border of its own) — so every
 * section head in the application is this one component, never a module's
 * re-implementation of it. `Card` is the head plus the card.
 *
 * `wash` lays `--card-wash` over the card ground. Use it on a page's FIRST
 * or primary card only; on every card it stops meaning anything.
 */

export interface CardHeadProps {
  /** A Phosphor icon, already sized (16 for `sm`, 20 for `md`). Decorative. */
  icon: ReactNode;
  title: ReactNode;
  /** The heading element. Defaults to `h2` (the shell owns the page `h1`). */
  level?: 'h2' | 'h3';
  size?: 'sm' | 'md';
  id?: string;
  /** Placed at the end of the head row: a command, a count, a chip. */
  aside?: ReactNode;
  className?: string;
}

export function CardHead({ icon, title, level = 'h2', size = 'sm', id, aside, className }: CardHeadProps) {
  const Heading = level;
  return (
    <div className={[styles['head'], className].filter(Boolean).join(' ')}>
      <IconDisc size={size}>{icon}</IconDisc>
      <Heading className={[styles['title'], styles[size]].join(' ')} id={id}>
        {title}
      </Heading>
      {aside === undefined ? null : <div className={styles['aside']}>{aside}</div>}
    </div>
  );
}

export interface CardProps extends Omit<CardHeadProps, 'id' | 'className'> {
  children?: ReactNode;
  wash?: boolean;
  /** The body runs edge to edge — for a table that belongs to this card. */
  flush?: boolean;
  /** The landmark element. `section` (named by its heading) by default. */
  as?: 'section' | 'div' | 'article';
  className?: string;
  'data-testid'?: string;
}

export function Card({ children, wash = false, flush = false, as = 'section', className, ...head }: CardProps) {
  const id = useId();
  const Element = as;
  const { icon, title, level, size, aside } = head;
  return (
    <Element
      className={[styles['card'], wash ? styles['wash'] : undefined, flush ? styles['flush'] : undefined, className].filter(Boolean).join(' ')}
      aria-labelledby={as === 'div' ? undefined : id}
      data-testid={head['data-testid']}
    >
      <CardHead icon={icon} title={title} level={level} size={size} aside={aside} id={id} />
      {children}
    </Element>
  );
}
